import { motion } from 'framer-motion';
import { Wallet, TrendingUp, TrendingDown, DollarSign, Users, ArrowUpRight, ArrowDownRight, RefreshCcw } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { dashboardApi, transactionsApi } from '@/lib/api';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

export default function WalletOverview() {
    const [stats, setStats] = useState<any>(null);
    const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, transactionsRes] = await Promise.all([
                dashboardApi.getStats(),
                transactionsApi.getTransactions({ limit: 5 })
            ]);

            if (statsRes.data.success) {
                setStats(statsRes.data.data);
            }

            if (transactionsRes.data.success) {
                setRecentTransactions(transactionsRes.data.data.transactions);
            }
        } catch (error) {
            console.error("Failed to fetch wallet overview data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const totalBalance = stats?.users?.totalBalance || 0;
    const totalDeposits = stats?.transactions?.totalDeposits || 0;
    const totalWithdrawals = stats?.transactions?.totalWithdrawals || 0;
    const activeWallets = stats?.users?.active || 0;
    const avgWalletBalance = activeWallets > 0 ? totalBalance / activeWallets : 0;
    
    // Growth metrics (using data if available, else 0)
    const balanceGrowth = 0; // Not currently tracked in history for total balance
    const depositGrowth = stats?.transactions?.depositGrowth || 0;
    const withdrawalGrowth = stats?.transactions?.withdrawalGrowth || 0;

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            {/* Header */}
            <motion.div variants={itemVariants} className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3">
                        <Wallet className="w-7 h-7 text-violet-400" />
                        Wallet Overview
                    </h1>
                    <p className="text-slate-400 mt-1">Monitor user wallet balances and transactions</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={fetchData} disabled={loading}>
                        <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card variant="glass" className="p-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm text-slate-400 font-medium">Total Balance</p>
                            <h3 className="text-2xl font-bold mt-2">{loading ? "..." : formatCurrency(totalBalance)}</h3>
                            <div className="flex items-center gap-1.5 mt-2">
                                <ArrowUpRight className={`w-4 h-4 ${balanceGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
                                <span className={`text-sm font-medium ${balanceGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {balanceGrowth >= 0 ? '+' : ''}{balanceGrowth}%
                                </span>
                                <span className="text-xs text-slate-500">vs last period</span>
                            </div>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                            <Wallet className="w-6 h-6 text-violet-400" />
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm text-slate-400 font-medium">Total Deposits</p>
                            <h3 className="text-2xl font-bold mt-2">{loading ? "..." : formatCurrency(totalDeposits)}</h3>
                            <div className="flex items-center gap-1.5 mt-2">
                                <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                                <span className="text-sm font-medium text-emerald-400">
                                    {depositGrowth > 0 ? '+' : ''}{depositGrowth}%
                                </span>
                                <span className="text-xs text-slate-500">this month</span>
                            </div>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-emerald-400" />
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm text-slate-400 font-medium">Total Withdrawals</p>
                            <h3 className="text-2xl font-bold mt-2">{loading ? "..." : formatCurrency(totalWithdrawals)}</h3>
                            <div className="flex items-center gap-1.5 mt-2">
                                <ArrowUpRight className="w-4 h-4 text-amber-400" />
                                <span className="text-sm font-medium text-amber-400">
                                    {withdrawalGrowth > 0 ? '+' : ''}{withdrawalGrowth}%
                                </span>
                                <span className="text-xs text-slate-500">this month</span>
                            </div>
                        </div>
                        <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                            <TrendingDown className="w-6 h-6 text-amber-400" />
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Secondary Stats */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Users className="w-6 h-6 text-blue-400" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-slate-400">Active Wallets</p>
                            <p className="text-2xl font-bold">{loading ? "..." : formatNumber(activeWallets)}</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-slate-400">Avg. Wallet Balance</p>
                            <p className="text-2xl font-bold">{loading ? "..." : formatCurrency(avgWalletBalance)}</p>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Recent Transactions */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold">Recent Transactions</h3>
                        <Button variant="ghost" size="sm">View All</Button>
                    </div>
                    <div className="space-y-4">
                        {loading && <p className="text-center text-slate-400 py-4">Loading transactions...</p>}
                        {!loading && recentTransactions.length === 0 && (
                            <p className="text-center text-slate-400 py-4">No recent transactions found.</p>
                        )}
                        {!loading && recentTransactions.map((transaction) => (
                            <div key={transaction._id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-800/30 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${transaction.type === 'deposit'
                                            ? 'bg-emerald-500/10 text-emerald-400'
                                            : 'bg-amber-500/10 text-amber-400'
                                        }`}>
                                        {transaction.type === 'deposit' ? (
                                            <ArrowDownRight className="w-5 h-5" />
                                        ) : (
                                            <ArrowUpRight className="w-5 h-5" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-medium">{transaction.userId?.name || transaction.userId?.phone || 'Unknown User'}</p>
                                        <p className="text-sm text-slate-400 capitalize">{transaction.type}</p>
                                    </div>
                                </div>
                                <div className="text-right flex items-center gap-4">
                                    <div>
                                        <p className={`font-semibold ${transaction.type === 'deposit' ? 'text-emerald-400' : 'text-white'
                                            }`}>
                                            {transaction.type === 'deposit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {new Date(transaction.createdAt).toLocaleDateString()} {new Date(transaction.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                    <Badge variant={transaction.status === 'completed' ? 'success' : 'warning'}>
                                        {transaction.status}
                                    </Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </motion.div>
        </motion.div>
    );
}
